import { Text } from '@airwallex/typography';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@airwallex/awx-currency-formatter';
import { IconChevronDown, IconChevronUp } from '@airwallex/glyphs';
import { cellContainerStyle, ShowMoreButton, LineItemsWrapper } from './v1/style';
import { useTranslation } from 'react-i18next';
import { useMobile, useTabletOrMobile } from '@/hooks/useMediaQuery';
import { VStack } from '@airwallex/stacks';
import { LineItems as LineItemsComponent, LineItemsColumn, LineItemsDataSource } from '@airwallex/line-items';
import DiscountCell from '@/common/DiscountCell';
import { CreditNoteLineItem } from '../types';
import { formatPriceCurrency } from '@billing/billing-domain-shared';

type LineItemColumnKey = 'item' | 'quantity' | 'price' | 'amount';

const SHOW_MORE_LENGTH = 4;

const buildDiscountRows = (item: CreditNoteLineItem): LineItemsDataSource<LineItemColumnKey>[] => {
  if (!item.lineItemDiscounts || item.lineItemDiscounts.length === 0) {
    return [];
  }
  return item.lineItemDiscounts.map((discount) => ({
    item: <DiscountCell discount={discount} variant="label" useVStack={false} />,
    quantity: null,
    price: null,
    amount: <DiscountCell discount={discount} variant="amount" currency={item.currency} useVStack={false} />,
  }));
};

const LineItems: React.FC<{ lineItemGroups: CreditNoteLineItem[] }> = ({ lineItemGroups }) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const isTabletOrMobile = useTabletOrMobile();
  const [showAllItems, setShowAllItems] = useState(false);

  const visibleGroups = useMemo(() => {
    if (isTabletOrMobile || lineItemGroups.length <= SHOW_MORE_LENGTH) {
      return lineItemGroups;
    }
    return showAllItems ? lineItemGroups : lineItemGroups.slice(0, SHOW_MORE_LENGTH);
  }, [isTabletOrMobile, lineItemGroups, showAllItems]);

  const showMoreButtonShown = useMemo(() => {
    if (isTabletOrMobile || lineItemGroups.length <= SHOW_MORE_LENGTH) {
      return false;
    }
    return lineItemGroups.length > SHOW_MORE_LENGTH;
  }, [lineItemGroups, isTabletOrMobile]);

  useEffect(() => {
    setShowAllItems(isMobile);
  }, [isMobile]);

  const hasQuantityInfo = useMemo(() => {
    return visibleGroups.some((item) => item.quantity && item.quantity > 0);
  }, [visibleGroups]);

  const fecColumns: LineItemsColumn<LineItemColumnKey>[] = useMemo(() => {
    return [
      { key: 'item', header: t('creditNote.item') },
      ...(hasQuantityInfo
        ? [
            { key: 'quantity' as const, header: t('creditNote.quantity') },
            { key: 'price' as const, header: t('creditNote.price'), align: 'right' as const },
          ]
        : []),
      { key: 'amount', header: t('creditNote.amount'), align: 'right' },
    ];
  }, [hasQuantityInfo, t]);

  const fecDataSources: LineItemsDataSource<LineItemColumnKey>[] = useMemo(() => {
    const rows: LineItemsDataSource<LineItemColumnKey>[] = [];

    visibleGroups.forEach((item) => {
      const hasItemQuantity = item.quantity && item.quantity > 0;

      rows.push({
        item: (
          <VStack style={cellContainerStyle}>
            <Text variant="body">{item.description ?? '-'}</Text>
          </VStack>
        ),
        ...(hasQuantityInfo
          ? {
              quantity: <Text variant="body">{hasItemQuantity ? item.quantity : '-'}</Text>,
              price: (
                <Text variant="body">
                  {hasItemQuantity
                    ? formatPriceCurrency({
                        amount: item.unitAmount ?? 0,
                        currency: item.currency,
                        showSymbol: true,
                        showCode: false,
                      })
                    : '-'}
                </Text>
              ),
            }
          : { quantity: null, price: null }),
        amount: (
          <Text variant="body">
            {formatCurrency({
              amount: item.amount,
              currency: item.currency,
              showSymbol: true,
              showCode: false,
            })}
          </Text>
        ),
      });

      rows.push(...buildDiscountRows(item));
    });

    if (showMoreButtonShown) {
      rows.push({
        item: (
          <ShowMoreButton
            iconAfter={showAllItems ? IconChevronUp : IconChevronDown}
            onClick={() => setShowAllItems(!showAllItems)}
          >
            {showAllItems ? t('creditNote.showLessLineItems') : t('creditNote.showMoreLineItems')}
          </ShowMoreButton>
        ),
        quantity: null,
        price: null,
        amount: '',
      });
    }

    return rows;
  }, [visibleGroups, t, showMoreButtonShown, showAllItems, hasQuantityInfo]);

  return (
    <LineItemsWrapper data-test="credit-note-line-items" showMoreButtonShown={showMoreButtonShown}>
      <LineItemsComponent isMobile={isTabletOrMobile} columns={fecColumns} dataSources={fecDataSources} />
    </LineItemsWrapper>
  );
};

export default LineItems;
